import type {InstantAnswer} from 'client';

import {analyticsLogger} from './analyticsLogger';

// Search instant-answer card events. They never carry the query text; the
// word count is enough to tell short lookups from long questions.

const baseProperties = (answer: InstantAnswer, query: string) => ({
  kind: answer.kind,
  source: answer.source,
  query_words: query.trim().split(/\s+/).filter(Boolean).length,
});

/** Whether the card's one-sentence answer was on screen, and how long it took. */
export type InstantAnswerSummaryState = {shown: boolean; latencyMs: number | null};

const summaryProperties = ({shown, latencyMs}: InstantAnswerSummaryState) => ({
  summary_shown: shown,
  ...(latencyMs !== null && {summary_ms: Math.round(latencyMs)}),
});

export const logInstantAnswerShown = ({
  answer,
  query,
  latencyMs,
}: {
  answer: InstantAnswer;
  query: string;
  latencyMs: number;
}) =>
  analyticsLogger().logEvent('SEARCH_INSTANT_ANSWER_SHOWN', {
    ...baseProperties(answer, query),
    latency_ms: Math.round(latencyMs),
  });

/** The one-sentence answer replaced its placeholder. */
export const logInstantAnswerSummaryShown = ({
  answer,
  query,
  latencyMs,
}: {
  answer: InstantAnswer;
  query: string;
  latencyMs: number;
}) =>
  analyticsLogger().logEvent('SEARCH_INSTANT_ANSWER_SUMMARY_SHOWN', {
    ...baseProperties(answer, query),
    ...summaryProperties({shown: true, latencyMs}),
  });

export const logInstantAnswerClicked = ({
  answer,
  query,
  summary,
}: {
  answer: InstantAnswer;
  query: string;
  summary: InstantAnswerSummaryState;
}) =>
  analyticsLogger().logEvent('SEARCH_INSTANT_ANSWER_CLICKED', {
    ...baseProperties(answer, query),
    ...summaryProperties(summary),
  });

export const logInstantAnswerAskClicked = ({
  answer,
  query,
  summary,
}: {
  answer: InstantAnswer;
  query: string;
  summary: InstantAnswerSummaryState;
}) =>
  analyticsLogger().logEvent('SEARCH_INSTANT_ANSWER_ASK_CLICKED', {
    ...baseProperties(answer, query),
    ...summaryProperties(summary),
  });
